import React, { Component } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Phone,
  Mail,
  Facebook,
  Instagram,
  Linkedin,
  ArrowUp,
  ArrowRight,
} from "lucide-react";
import "./index.css";

class Footer extends Component {
  componentDidMount() {
    window.addEventListener("scroll", this.handleScrollVisibility);

    const footerElement = document.querySelector(".Footer");
    if (footerElement) {
      footerElement.classList.add("Footer-visible");
    }
  }

  componentWillUnmount() {
    // Clean up scroll event listener
    window.removeEventListener("scroll", this.handleScrollVisibility);
  }

  handleScrollVisibility = () => {
    const scrollButton = document.querySelector(".Footer-scroll-top");
    if (scrollButton) {
      if (window.scrollY > 300) {
        scrollButton.classList.add("visible");
      } else {
        scrollButton.classList.remove("visible");
      }
    }
  };

  handleScrollToSection = (e) => {
    e.preventDefault();
    const href = e.currentTarget.getAttribute("href") || "";
    const targetId = href.replace("#", "");
    const element = document.getElementById(targetId);

    if (element) {
      const yOffset = -80; // Adjust based on navbar height
      const y =
        element.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle phone call
  handlePhoneClick = () => {
    window.open("tel:+13302345251", "_self");
  };

  // Handle email
  handleEmailClick = () => {
    window.open("mailto:swlwenterprise@gmail.com", "_self");
  };

  // Handle location/maps
  handleLocationClick = () => {
    const address = "11 Overhill Rd, Boardman, OH, United States, Ohio";
    const encodedAddress = encodeURIComponent(address);
    const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    window.open(googleMapsUrl, "_blank", "noopener,noreferrer");
  };

  render() {
    const quickLinks = [
      { label: "About Us", href: "#about" },
      { label: "Services", href: "#services" },
      { label: "Portfolio", href: "#portfolio" },
      { label: "Gallery", href: "#gallery" },
    ];

    // Updated business links with appropriate IDs that match the service cards
    const businesses = [
      { label: "Crawling to Destiny", href: "#crawling" },
      { label: "New Vision Behavioral Health", href: "#newvision" },
      { label: "Hitchcock Woods Learning", href: "#hitchcock" },
      { label: "Youngstown Event Center", href: "#youngstown" },
    ];

    // Updated contact info with click handlers
    const contactInfo = [
      {
        icon: <MapPin className="Footer-contact-icon" />,
        text: "80 E Midlothian Blvd, Youngstown, OH, United States, Ohio",
        onClick: this.handleLocationClick,
        title: "Click to view on Google Maps",
        isClickable: true,
      },
      {
        icon: <Phone className="Footer-contact-icon" />,
        text: "(330) 967 8874",
        onClick: this.handlePhoneClick,
        title: "Click to call",
        isClickable: true,
      },
      {
        icon: <Mail className="Footer-contact-icon" />,
        text: "swlwenterprise@gmail.com",
        onClick: this.handleEmailClick,
        title: "Click to send email",
        isClickable: true,
      },
    ];

    // Updated social links with the correct URLs
    const socialLinks = [
      {
        href: "https://www.facebook.com/wlwenterpriseohio/",
        icon: <Facebook className="Footer-social-icon" />,
      },
      {
        href: "https://www.instagram.com/wlwenterprise/",
        icon: <Instagram className="Footer-social-icon" />,
      },
      {
        href: "https://www.linkedin.com/in/wlw-enterprise-753a98365/",
        icon: <Linkedin className="Footer-social-icon" />,
      },
    ];

    const footerClassName = "Footer";

    return (
      <footer className={footerClassName}>
        <div className="Footer-container">
          <div className="Footer-grid">
            {/* Company Info Column */}
            <div className="Footer-column">
              <Link to="/" className="Footer-logo-link">
                <img
                  src="/Images/WLW_Logo_PNG.png"
                  alt="W.L.W. Enterprise Logo"
                  className="Footer-logo"
                />
              </Link>
              <p className="Footer-description">
                Empowering businesses through strategic marketing, promotion,
                and engagement. We pride ourselves on our ability to tailor
                solutions that meet the unique needs of each business.
              </p>
              <div className="Footer-social-links">
                {socialLinks.map((social, index) => (
                  <a
                    key={index}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="Footer-social-link"
                  >
                    {social.icon}
                  </a>
                ))}
              </div>
            </div>

            {/* Quick Links Column */}
            <div className="Footer-column">
              <h3 className="Footer-heading">
                Quick Links
                <span className="Footer-heading-underline"></span>
              </h3>
              <ul className="Footer-link-list">
                {quickLinks.map((link, index) => (
                  <li key={index} className="Footer-link-item">
                    <a
                      href={link.href}
                      onClick={this.handleScrollToSection}
                      className="Footer-link"
                    >
                      <span className="Footer-arrow">›</span>
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Our Businesses Column */}
            <div className="Footer-column">
              <h3 className="Footer-heading">
                Our Business
                <span className="Footer-heading-underline"></span>
              </h3>
              <ul className="Footer-link-list">
                {businesses.map((business, index) => (
                  <li key={index} className="Footer-link-item">
                    <a
                      href={business.href}
                      onClick={this.handleScrollToSection}
                      className="Footer-link"
                    >
                      <span className="Footer-arrow">›</span>
                      {business.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Contact Column */}
            <div className="Footer-column">
              <h3 className="Footer-heading">
                Contact Us
                <span className="Footer-heading-underline"></span>
              </h3>
              <ul className="Footer-contact-list">
                {contactInfo.map((info, index) => (
                  <li key={index} className="Footer-contact-item">
                    {info.isClickable ? (
                      <button
                        onClick={info.onClick}
                        className="Footer-contactsec-button"
                        title={info.title}
                      >
                        <span className="Footer-contact-icon-wrapper">
                          {info.icon}
                        </span>
                        <span className="Footer-contact-text">{info.text}</span>
                      </button>
                    ) : (
                      <>
                        <span className="Footer-contact-icon-wrapper">
                          {info.icon}
                        </span>
                        <span className="Footer-contact-text">{info.text}</span>
                      </>
                    )}
                  </li>
                ))}
              </ul>
              <a
                href="#appointment"
                onClick={this.handleScrollToSection}
                className="Footer-appointment-button"
              >
                <span className="Footer-button-text">
                  Make an Appointment
                  <ArrowRight className="Footer-button-arrow" />
                </span>
              </a>
            </div>
          </div>

          <div className="Footer-bottom">
            <p className="Footer-copyright">
              &copy; {new Date().getFullYear()} W.L.W. Enterprise. All rights
              reserved.
            </p>

            <button
              onClick={this.handleScrollToTop}
              className="Footer-scroll-top"
              aria-label="Scroll to top"
            >
              <ArrowUp className="Footer-scroll-top-icon" />
            </button>
          </div>
        </div>
      </footer>
    );
  }
}

export default Footer;
